import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Logo, LogoMark } from "@/components/Logo";
import MarketTicker from "@/components/MarketTicker";
import {
  ArrowRight, ShieldCheck, MessageSquareText, Layers, LineChart,
  Landmark, Coins, PieChart, Building2, TrendingUp, Sparkles,
} from "lucide-react";

const PIPELINE = [
  { icon: LineChart, label: "Stocks" },
  { icon: Layers, label: "Mutual Funds" },
  { icon: Landmark, label: "Bonds" },
  { icon: Coins, label: "Gold" },
  { icon: PieChart, label: "ETFs" },
  { icon: Building2, label: "PPF & FDs" },
];

const STEPS = [
  { n: "01", t: "Tell us your goal", d: "Share your amount, timeline and how you feel about risk — in plain language, no jargon." },
  { n: "02", t: "AI researches for you", d: "Invesy compares mutual funds, bonds, gold, FDs and ETFs suited to you." },
  { n: "03", t: "Understand & decide", d: "Get a simple, ranked plan with confidence scores, honest risks and an 'Explain simply' for everything." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo size={30} />
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid="nav-login-btn" className="rounded-full text-slate-600">Log in</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="nav-signup-btn" className="rounded-full bg-slate-900 hover:bg-slate-800 hover:-translate-y-0.5 transition-transform duration-300">
                Get started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Market strip */}
      <div className="border-b border-slate-200 bg-white/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-3 overflow-x-auto scrollbar-thin">
          <MarketTicker />
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[60%] h-[600px] bg-gradient-to-b from-indigo-100/60 to-transparent blur-3xl -z-0" />
        <div className="max-w-6xl mx-auto px-6 pt-20 pb-14 grid lg:grid-cols-2 gap-14 items-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-semibold mb-6 soft-shadow">
              <Sparkles size={14} className="text-indigo-500" /> AI investment research, made simple
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
              Invest smarter.
              <br />
              Without becoming a<br />
              <span className="text-indigo-600">financial expert.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-500 max-w-md leading-relaxed">
              Tell Invesy your goal, budget and timeline. Our AI researches investment options and explains them in simple language.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" data-testid="hero-primary-cta" className="rounded-full h-12 px-7 gap-2 bg-slate-900 hover:bg-slate-800 hover:-translate-y-0.5 transition-transform duration-300">
                  Start My Investment Plan <ArrowRight size={18} />
                </Button>
              </Link>
              <a href="#how">
                <Button size="lg" variant="outline" data-testid="hero-secondary-cta" className="rounded-full h-12 px-7 border-slate-300 bg-white">
                  Explore How It Works
                </Button>
              </a>
            </div>
            <p className="mt-5 text-xs text-slate-400 flex items-center gap-1.5">
              <ShieldCheck size={14} /> No investment is guaranteed. Educational, jargon-free guidance.
            </p>
          </motion.div>

          {/* Hero visual: AI research assistant analysing instruments */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 soft-shadow-lg">
              <div className="flex items-center gap-3 mb-5">
                <LogoMark size={36} />
                <div>
                  <p className="font-heading font-semibold text-slate-900">Invesy is researching…</p>
                  <p className="text-xs text-slate-400">Analysing options for ₹50,000</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2.5 mb-5">
                {PIPELINE.map((p, i) => (
                  <motion.div key={p.label}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.1 }}
                    className="rounded-2xl border border-slate-100 bg-slate-50 p-3 flex flex-col items-center gap-1.5">
                    <p.icon size={20} strokeWidth={1.75} className="text-indigo-500" />
                    <span className="text-[11px] font-medium text-slate-500 text-center leading-tight">{p.label}</span>
                  </motion.div>
                ))}
              </div>
              <div className="rounded-2xl bg-slate-900 p-4 text-white">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-slate-400">Suggested split</span>
                  <span className="text-xs font-semibold text-indigo-300 flex items-center gap-1">
                    <ShieldCheck size={13} /> 84% confidence
                  </span>
                </div>
                {[
                  { l: "Nifty 50 Index Fund", v: "35%", c: "#6366F1" },
                  { l: "Short-term Debt Fund", v: "25%", c: "#10B981" },
                  { l: "Gold ETF", v: "15%", c: "#F59E0B" },
                ].map((r) => (
                  <div key={r.l} className="flex items-center gap-2 py-1.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.c }} />
                    <span className="text-sm flex-1 text-slate-200">{r.l}</span>
                    <span className="font-mono text-sm">{r.v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 rounded-2xl bg-white border border-slate-200 soft-shadow p-4 hidden sm:flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <TrendingUp size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Explained simply</p>
                <p className="text-sm font-semibold">Like ordering online</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pipeline strip */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-3 text-slate-400">
          {PIPELINE.map((p, i) => (
            <React.Fragment key={p.label}>
              <span className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <p.icon size={16} strokeWidth={1.75} className="text-indigo-500" /> {p.label}
              </span>
              {i < PIPELINE.length - 1 && <ArrowRight size={14} className="text-slate-300" />}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-6xl mx-auto px-6 py-24">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500 mb-3">How it works</p>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight max-w-xl leading-snug">
          From “where do I even start?” to a clear plan in minutes.
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {STEPS.map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="rounded-3xl border border-slate-200 bg-white p-8 soft-shadow">
              <span className="font-mono text-sm text-indigo-500 font-semibold">{s.n}</span>
              <h3 className="font-heading font-semibold text-xl mt-4 mb-2">{s.t}</h3>
              <p className="text-slate-500 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust / features */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: MessageSquareText, t: "Plain language, always", d: "Every complex idea comes with an 'Explain simply' option. No confusing jargon." },
            { icon: ShieldCheck, t: "Honest about risk", d: "We show confidence scores and potential downsides. No fake promises — no investment is guaranteed." },
            { icon: TrendingUp, t: "Track your health", d: "See your Financial Health Score and get simple tips to improve it over time." },
          ].map((f, i) => (
            <motion.div key={f.t} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="rounded-3xl border border-slate-200 bg-white p-8 soft-shadow">
              <div className="h-11 w-11 rounded-2xl bg-indigo-50 flex items-center justify-center mb-5">
                <f.icon size={22} strokeWidth={1.75} className="text-indigo-600" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">{f.t}</h3>
              <p className="text-slate-500 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="rounded-3xl bg-slate-900 p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-indigo-500/30 blur-3xl" />
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-white relative z-10">
            Your first investment plan is a tap away.
          </h2>
          <p className="text-slate-300 mt-4 max-w-md mx-auto relative z-10">
            Join Invesy and let AI do the research. You stay in control.
          </p>
          <Link to="/register" className="relative z-10 inline-block mt-8">
            <Button size="lg" data-testid="cta-bottom" className="rounded-full h-12 px-8 gap-2 bg-white text-slate-900 hover:bg-slate-100 hover:-translate-y-0.5 transition-transform duration-300">
              Start My Investment Plan <ArrowRight size={18} />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size={26} tagline />
          <p className="text-xs text-slate-400 text-center">
            Educational guidance, not registered investment advice. No investment is guaranteed.
          </p>
        </div>
      </footer>
    </div>
  );
}
